import { Button, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ReactElement, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCanAccess, useNotify } from 'react-admin';
import { useReportClickMerchantCreate } from '../../../../DataProvider';
import type { ClicksByMerchantsResource } from '../../../../Entities/ClicksByMerchant';
import { getReportDateLabels } from '../../../../Components/ReportsWidget/utils';
import { getClicksByMerchantReportParams, type Filter } from './utils';

export interface PreviewButtonProps {
  onPreviewStart: (labels: { startDateLabel: string; endDateLabel: string }) => void;
  onPreviewSuccess: (records: ClicksByMerchantsResource[]) => void;
  onPreviewError: () => void;
  autoPreviewOnMount?: boolean;
}

export default function PreviewButton({
  onPreviewStart,
  onPreviewSuccess,
  onPreviewError,
  autoPreviewOnMount = false,
}: PreviewButtonProps): ReactElement | null {
  const { handleSubmit } = useFormContext();
  const notify = useNotify();
  const [runReport, report] = useReportClickMerchantCreate();
  const hasAutoPreviewTriggered = useRef(false);

  const reportsCreateAccess = useCanAccess({ resource: 'reports', action: 'create' });
  const clicksByMerchantsListAccess = useCanAccess({
    resource: 'clicks_by_merchants',
    action: 'list',
  });

  const handlePreview = handleSubmit(async (values) => {
    if (report.isPending) {
      return;
    }

    const filter = values as Filter;
    const { startDate, endDate } = getReportDateLabels(filter);
    onPreviewStart({ startDateLabel: startDate, endDateLabel: endDate });

    try {
      const result = await runReport(getClicksByMerchantReportParams(filter));
      onPreviewSuccess(result.data);
    } catch (error) {
      onPreviewError();
      notify(error instanceof Error ? error.message : 'Preview failed', { type: 'error' });
    }
  });

  useEffect(() => {
    if (
      !autoPreviewOnMount ||
      hasAutoPreviewTriggered.current ||
      !reportsCreateAccess.canAccess ||
      !clicksByMerchantsListAccess.canAccess
    ) {
      return;
    }

    hasAutoPreviewTriggered.current = true;
    void handlePreview();
  }, [
    autoPreviewOnMount,
    clicksByMerchantsListAccess.canAccess,
    handlePreview,
    reportsCreateAccess.canAccess,
  ]);

  if (!reportsCreateAccess.canAccess || !clicksByMerchantsListAccess.canAccess) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      onClick={() => void handlePreview()}
      disabled={report.isPending}
      startIcon={
        report.isPending ? <CircularProgress size={16} /> : <VisibilityIcon fontSize="small" />
      }
    >
      {report.isPending ? 'Loading…' : 'Preview'}
    </Button>
  );
}
